import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["title"]
const _hoisted_2 = {
  key: 1,
  class: "px-4"
}

import { ref, watch, Ref, computed } from 'vue'
import {
  AddressLevel,
  AddressSuggestion,
  AddressSuggesterService,
  AddressSuggestionSearchOptions,
} from '@prodcard/core'
import { debounce, values, filter, pickBy, isEqual, assign } from 'lodash'
import { createCtx, handleError } from '../helpers'

interface Props {
  addressSuggesterService: AddressSuggesterService
  modelValue?: string
  municipalEntityId?: string
  level: AddressLevel
  label?: string
  otherFields?: Partial<Record<AddressLevel, string>>
  errorMessages?: string | readonly string[]
  readonly?: boolean
}

const count = 15

// loaders

export default /*#__PURE__*/_defineComponent({
  __name: 'AddressSuggester',
  props: {
    addressSuggesterService: {},
    modelValue: {},
    municipalEntityId: {},
    level: {},
    label: {},
    otherFields: {},
    errorMessages: {},
    readonly: { type: Boolean, default: false }
  },
  emits: ["update:model-value", "update:selected-value"],
  setup(__props: any, { emit }) {

const props = __props;





const suggestList = ref<AddressSuggestion[]>([])
const focused = ref(false)
const isTouchFilter = ref(false)
const currentItem = computed(() => {
  const suggests = filter(suggestList.value, s =>
    isEqual(s.address, {
      ...props.otherFields,
      [props.level]: props.modelValue,
    }),
  )
  return suggests.length === 0 || suggests.length > 1 ? undefined : suggests[0]
})

// paging
const loadMore = ref(false)
const from = ref(0)
const total = ref(0)
const loading = ref(false)
const initialLoading = ref(false)

const suggest = (
  opts: { search: string; paging: { from: number; count: number } },
  replace = false,
  loading?: Ref<boolean>,
) => {
  handleError(async () => {
    const response = await fetchData({
      search: opts.search,
      paging: opts.paging,
      level: props.level,
      otherFields: pickBy(props.otherFields, f => f),
      municipalEntityId: props.municipalEntityId,
    })

    if (replace) {
      suggestList.value = response.items
    } else {
      suggestList.value.push(...response.items)
    }

    loadMore.value = suggestList.value.length < response.total
    from.value += opts.paging.count
    if (total.value !== response.total) total.value = response.total
  }, loading)
}

const fetchData = async (opts: AddressSuggestionSearchOptions) => {
  const response = await props.addressSuggesterService.suggest(createCtx(), opts)
  return response
}

const selectSuggest = (suggest: AddressSuggestion) => {
  loadMore.value = false
  suggestList.value = [suggest]
  emit('update:selected-value', suggest)
}

const aggregateSuggest = (suggest: AddressSuggestion) => {
  const addressSuggest: Partial<Record<AddressLevel, string>> = {}

  if (props.level === AddressLevel.HOUSE) {
    addressSuggest[AddressLevel.HOUSE] = suggest.address[AddressLevel.HOUSE]
  } else if (props.level === AddressLevel.STREET && props.otherFields?.[AddressLevel.LOCALITY]) {
    addressSuggest[AddressLevel.STREET] = suggest.address[AddressLevel.STREET]
  } else {
    assign(addressSuggest, suggest.address)
  }

  return values(addressSuggest)
    .filter(s => s)
    .join(', ')
}

const updateModelValue = (value: string | AddressSuggestion) => {
  if (typeof value === 'string') {
    emit('update:model-value', value)
  } else if (value === null) {
    emit('update:model-value', '')
  }
}

const updateMenuValue = (isOpen: boolean) => {
  if (!isOpen) {
    if (isTouchFilter.value) isTouchFilter.value = false
    return
  }

  if (isTouchFilter.value) {
    isTouchFilter.value = false
    suggestList.value = []
  }

  if (suggestList.value.length === 0) {
    from.value = 0
    suggest({ search: props.modelValue?.trim() || '', paging: { from: from.value, count } }, true, initialLoading)
  }
}

const endIntersect = (isIntersecting: boolean) => {
  if (isIntersecting) suggest({ search: props.modelValue?.trim() || '', paging: { from: from.value, count } })
}

watch(
  () => props.modelValue,
  debounce(val => {
    if (!focused.value) return
    from.value = 0
    suggest({ search: val.trim(), paging: { from: from.value, count } }, true, loading)
  }, 300),
)

watch(
  () => [props.municipalEntityId, props.otherFields, props.modelValue],
  () => {
    isTouchFilter.value = true
  },
)

return (_ctx: any,_cache: any) => {
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_combobox = _resolveComponent("v-combobox")!
  const _directive_intersect = _resolveDirective("intersect")!

  return (_openBlock(), _createBlock(_component_v_combobox, {
    "model-value": _ctx.modelValue || undefined,
    label: _ctx.label,
    items: suggestList.value,
    "error-messages": _ctx.errorMessages,
    "hide-no-data": false,
    "menu-props": {
      maxWidth: 0,
      closeOnContentClick: true,
    },
    autocomplete: "do-not-autofill",
    clearable: !_ctx.readonly,
    "no-filter": "",
    onFocus: _cache[0] || (_cache[0] = ($event: any) => (focused.value = true)),
    onBlur: _cache[1] || (_cache[1] = ($event: any) => (focused.value = false)),
    "onUpdate:modelValue": updateModelValue,
    "onUpdate:menu": updateMenuValue
  }, {
    item: _withCtx(({ item }) => [
      _createElementVNode("div", {
        title: aggregateSuggest(item.value)
      }, [
        _createVNode(_component_v_list_item, {
          value: item.value,
          active: _unref(isEqual)(item.value.address, currentItem.value?.address),
          onClick: ($event: any) => (selectSuggest(item.value))
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_list_item_title, null, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(aggregateSuggest(item.value)), 1)
              ]),
              _: 2
            }, 1024)
          ]),
          _: 2
        }, 1032, ["value", "active", "onClick"])
      ], 8, _hoisted_1)
    ]),
    "append-inner": _withCtx(() => [
      (loading.value)
        ? (_openBlock(), _createBlock(_component_v_progress_circular, {
            key: 0,
            indeterminate: "",
            color: "primary",
            size: "16",
            width: "2"
          }))
        : _createCommentVNode("", true)
    ]),
    "append-item": _withCtx(() => [
      (loadMore.value && !initialLoading.value)
        ? _withDirectives((_openBlock(), _createBlock(_component_v_btn, {
            key: 0,
            variant: "text",
            width: "100%",
            loading: ""
          }, null, 512)), [
            [_directive_intersect, endIntersect]
          ])
        : _createCommentVNode("", true)
    ]),
    "no-data": _withCtx(() => [
      (initialLoading.value && suggestList.value.length === 0)
        ? (_openBlock(), _createBlock(_component_v_btn, {
            key: 0,
            variant: "text",
            width: "100%",
            loading: ""
          }))
        : (_openBlock(), _createElementBlock("p", _hoisted_2, "Нет данных"))
    ]),
    _: 1
  }, 8, ["model-value", "label", "items", "error-messages", "clearable"]))
}
}

})